import { onMounted, onUnmounted, ref, watch } from 'vue'
import store from '@/store'
import { fetchSupportUnreadCount } from '@/utils/supportApi'
import { getEcho } from '@/utils/echo'

/**
 * Глобальный счётчик непрочитанных тикетов поддержки + browser-push на
 * новое сообщение от staff'а.
 *
 * Зачем модульный singleton:
 *   - composable вызывается в нескольких местах (App.vue / MainHeader / Sidebar)
 *     и при логине/смене страницы; один WS-канал на всю сессию + один источник
 *     истины для бейджа.
 *
 * Источники обновления (по приоритету):
 *   1. WebSocket `support.user.{userId}` событие `support.unread.updated` —
 *      основной канал, прилетает сразу после `addMessage(staff=true)` на бэке.
 *   2. Первичный HTTP `GET /api/support/tickets/unread-count` — один раз при
 *      первой подписке на сессию (warm-up бейджа). На бэке закеширован,
 *      поэтому БД не нагружается.
 *   3. Fallback polling раз в 60с — страховка на случай если WS лёг (CORS /
 *      прокси / Reverb упал). На основном happy-path не выполняется.
 *
 * Локальное обновление без БД:
 *   - WS-payload уже содержит свежий count, поэтому при апдейте мы НЕ дёргаем
 *     `fetchSupportUnreadCount()` повторно. Это и есть «не тянуть из базы при
 *     каждом пуше».
 */

const unreadCount = ref(0)
const lastMessage = ref(null)
/**
 * Реактивный «патч тикета», прилетевший в WS-payload `support.unread.updated`
 * (поле `ticket`). Используется компонентами поддержки (модалка/список) для
 * live-обновления статуса и `replied_at` открытого тикета БЕЗ HTTP-добора.
 *
 * Каждое новое событие — это новый объект ref'а (а не мутация), чтобы `watch`
 * корректно срабатывал даже если бэк прислал тот же ticket_id два раза подряд
 * (например, ответ staff'а сразу + смена статуса — внутри двух разных пушей).
 */
const lastTicketPatch = ref(null)
let subscribers = 0
let echoChannel = null
let echoUserId = null
let fallbackTimer = null
let warmedUpForUserId = null
let userIdWatcherStop = null
let notificationAudio = null
let titleFlashTimer = null
let originalDocTitle = null
/**
 * Все живые desktop-Notification, выданные `showDesktopNotification`. Без
 * явного `.close()` они висят в Notification Center ОС и рисуют бейдж на
 * иконке браузера в Dock/Trayзе. При обнулении счётчика чистим пачкой.
 */
const liveNotifications = new Set()

function showDesktopNotification(payload) {
    if (typeof window === 'undefined' || !('Notification' in window)) return
    if (Notification.permission !== 'granted') return
    const bodyPreview = typeof payload?.message?.body === 'string'
        ? payload.message.body.slice(0, 140)
        : 'У вас новое сообщение в обращениях'
    try {
        const notif = new Notification('Поддержка ответила', {
            body: bodyPreview,
            icon: '/favicon.svg',
            tag: payload?.ticket_id ? `support-ticket-${payload.ticket_id}` : 'support-unread',
            renotify: true,
        })
        liveNotifications.add(notif)
        notif.onclose = () => { liveNotifications.delete(notif) }
        // Клик по уведомлению — фокусируем вкладку, чтобы пользователь
        // мог открыть «Обращения» вручную. Глубокий деплинк не делаем,
        // т.к. модалка тикетов открывается только из ChatPage.
        notif.onclick = () => {
            try { window.focus() } catch { /* noop */ }
            try { notif.close() } catch { /* noop */ }
            liveNotifications.delete(notif)
        }
    } catch {
        // noop — на iOS Safari Notification.constructor может бросить
    }
}

/**
 * Полная очистка визуальных «следов» непрочитанных: закрываем все висящие
 * desktop-Notification (бейдж на иконке браузера снимается ОС автоматически
 * как только все нотификации закрыты), сбрасываем app badge и title-flash.
 */
function clearAllNotifications() {
    for (const n of liveNotifications) {
        try { n.close() } catch { /* noop */ }
    }
    liveNotifications.clear()
    if (typeof navigator !== 'undefined' && typeof navigator.clearAppBadge === 'function') {
        navigator.clearAppBadge().catch(() => {})
    }
    stopTitleFlash()
}

function playPingSound() {
    if (typeof window === 'undefined') return
    try {
        if (!notificationAudio) {
            // Лёгкий WebAudio-пинг без аудио-файла, чтобы не тащить mp3.
            const Ctx = window.AudioContext || window.webkitAudioContext
            if (!Ctx) return
            notificationAudio = new Ctx()
        }
        const ctx = notificationAudio
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = 880
        gain.gain.setValueAtTime(0.0001, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25)
        osc.connect(gain).connect(ctx.destination)
        osc.start()
        osc.stop(ctx.currentTime + 0.26)
    } catch {
        // Браузер мог заблокировать AudioContext до user gesture — игнорируем.
    }
}

function startTitleFlash(count) {
    if (typeof document === 'undefined') return
    if (originalDocTitle === null) {
        originalDocTitle = document.title
    }
    stopTitleFlash(/* keepOriginal */ true)
    const flashTitle = `(${count}) Новое сообщение поддержки`
    let toggle = false
    titleFlashTimer = setInterval(() => {
        if (typeof document === 'undefined') return
        document.title = toggle ? originalDocTitle : flashTitle
        toggle = !toggle
    }, 1500)
    // Останавливаем мигание как только пользователь вернулся во вкладку.
    if (typeof window !== 'undefined') {
        const onVisible = () => {
            if (!document.hidden) {
                stopTitleFlash()
                window.removeEventListener('focus', onVisible)
                document.removeEventListener('visibilitychange', onVisible)
            }
        }
        window.addEventListener('focus', onVisible)
        document.addEventListener('visibilitychange', onVisible)
    }
}

function stopTitleFlash(keepOriginal = false) {
    if (titleFlashTimer) {
        clearInterval(titleFlashTimer)
        titleFlashTimer = null
    }
    if (!keepOriginal && originalDocTitle !== null && typeof document !== 'undefined') {
        document.title = originalDocTitle
        originalDocTitle = null
    }
}

async function refresh() {
    try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : ''
        if (!token) {
            unreadCount.value = 0
            warmedUpForUserId = null
            return
        }
        const data = await fetchSupportUnreadCount()
        unreadCount.value = Number(data?.count ?? 0)
    } catch {
        // не сбрасываем счётчик в 0 чтобы не моргать при временном фейле
    }
}

function resolveUserId() {
    return store?.state?.main?.userInfo?.id
        || store?.state?.userInfo?.id
        || null
}

function subscribeWs() {
    const userId = resolveUserId()
    if (!userId) return false
    if (echoChannel && echoUserId === userId) return true

    teardownWs()
    try {
        const echo = getEcho()
        const channelName = `support.user.${userId}`
        const channel = echo.private(channelName)
        // broadcastAs() на бэке = 'support.unread.updated', поэтому Echo требует
        // точку перед именем — иначе он подставляет неймспейс App\Events\…
        channel.listen('.support.unread.updated', (payload) => {
            applyServerPayload(payload)
        })
        echoChannel = channel
        echoUserId = userId
        return true
    } catch {
        return false
    }
}

/**
 * Применяет payload из WS прямо к локальному состоянию, БЕЗ обращения к БД.
 * Это и есть основной режим работы — БД дёргается только на warm-up при первой
 * подписке за сессию (см. `ensureWarmedUp`).
 */
function applyServerPayload(payload) {
    const prev = unreadCount.value
    const next = Number(payload?.count ?? payload?.unread_count ?? 0)
    unreadCount.value = next
    lastMessage.value = payload?.message || null

    // Срез тикета (status/replied_at/...) — публикуем для подписчиков.
    // Создаём новый объект, чтобы watch стрелял даже на «одинаковых» апдейтах.
    if (payload?.ticket) {
        lastTicketPatch.value = {
            ...payload.ticket,
            // ticket_id может быть в payload и не в payload.ticket — нормализуем
            id: payload.ticket.id ?? payload.ticket_id ?? null,
            _receivedAt: Date.now(),
        }
    }

    // Звук/нотификация — ТОЛЬКО на новое staff-сообщение (есть `message`).
    // Смена статуса (`ticket` без `message`) — это не повод дергать юзера
    // звуком и desktop-нотифом, патчим тихо.
    if (next > prev && payload?.message) {
        showDesktopNotification(payload)
        playPingSound()
        if (typeof document !== 'undefined' && document.hidden) {
            startTitleFlash(next)
        }
        if (typeof navigator !== 'undefined' && typeof navigator.setAppBadge === 'function') {
            navigator.setAppBadge(next).catch(() => {})
        }
    }
    if (next === 0) {
        // Снимаем всё, что давало «висящий счётчик»: и title, и нотификации
        // в трее (это и есть цифра на иконке браузера у админа).
        clearAllNotifications()
    } else if (typeof navigator !== 'undefined' && typeof navigator.setAppBadge === 'function') {
        navigator.setAppBadge(next).catch(() => {})
    }
}

function teardownWs() {
    if (echoChannel && echoUserId) {
        try {
            getEcho().leave(`support.user.${echoUserId}`)
        } catch {
            // noop
        }
    }
    echoChannel = null
    echoUserId = null
}

function startFallbackPolling() {
    if (fallbackTimer) return
    // 60 секунд — не агрессивно, потому что это страховка на случай если
    // WS-соединение упало (CORS / прокси) — UX ответом «через минуту» вполне
    // ок, основной канал — push.
    fallbackTimer = setInterval(refresh, 60000)
}

function stopFallbackPolling() {
    if (fallbackTimer) {
        clearInterval(fallbackTimer)
        fallbackTimer = null
    }
}

/**
 * Запросить HTTP-счётчик один раз за сессию (по конкретному userId). Это
 * единственный поход в БД на холодный старт; дальше всё живёт на WS.
 */
function ensureWarmedUp() {
    const userId = resolveUserId()
    if (!userId || warmedUpForUserId === userId) return
    warmedUpForUserId = userId
    refresh()
}

/**
 * Глобальный bootstrap — вызывается ровно один раз из App.vue / Header,
 * чтобы счётчик был доступен на любой странице (не только в ChatPage).
 *
 * Идempotent: повторные вызовы только инкрементят счётчик подписчиков и
 * убедятся в актуальности WS-канала под текущим userId (на случай login).
 */
export function useSupportUnread() {
    onMounted(() => {
        subscribers += 1
        ensureWarmedUp()
        if (!subscribeWs()) {
            // userId ещё не подгрузился (например, App смонтировался раньше
            // чем fetchUser завершился) — подождём появления и подпишемся.
            setTimeout(() => subscribeWs(), 500)
        }
        startFallbackPolling()
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().catch(() => {})
        }
        // Если userInfo появился после монтирования (логин в SPA) — пере-warm.
        if (!userIdWatcherStop) {
            userIdWatcherStop = watch(
                () => resolveUserId(),
                (next, prev) => {
                    if (next && next !== prev) {
                        warmedUpForUserId = null
                        ensureWarmedUp()
                        subscribeWs()
                    }
                    if (!next) {
                        // logout — очистим состояние, иначе старый счётчик
                        // будет «висеть» на новом анонимном сеансе.
                        unreadCount.value = 0
                        lastMessage.value = null
                        warmedUpForUserId = null
                        clearAllNotifications()
                        teardownWs()
                    }
                },
            )
        }
    })

    onUnmounted(() => {
        subscribers -= 1
        if (subscribers <= 0) {
            subscribers = 0
            stopFallbackPolling()
            teardownWs()
            clearAllNotifications()
            if (userIdWatcherStop) {
                userIdWatcherStop()
                userIdWatcherStop = null
            }
        }
    })

    return {
        unreadCount,
        lastMessage,
        lastTicketPatch,
        refreshUnread: refresh,
    }
}
